import { useEffect, useState } from 'react';
import {
    Box, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, TextField, Tooltip, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import MDButton from '@components/MDButton';
import { useAlert } from '@components/showAlert/useAlertHook';
import { useMaterialUIController } from 'context';
import { useTranslation } from 'react-i18next';
import { apiErrorMessage } from '@functions/api-error-message';
import { getCrmColors } from '../../kanban-board/crm-theme';
import { LeadAttachment } from '../services/lead-attachment.service';
import { LeadSignatureService } from '../services/lead-signature.service';

/// O mesmo teto do servidor.
const MAX_SIGNERS = 10;

interface Props {
    leadId: string;
    attachment: LeadAttachment | null;
    signerName?: string | null;
    signerEmail?: string | null;
    onClose: () => void;
    onSent: () => void;
}

interface SignerRow {
    name: string;
    email: string;
}

/// Manda um PDF ao Clicksign; cada signatário recebe por e-mail e o assinado volta como anexo.
export const LeadSignatureModal = ({ leadId, attachment, signerName, signerEmail, onClose, onSent }: Props) => {
    const { t } = useTranslation();
    const [controller] = useMaterialUIController();
    const cc = getCrmColors(controller.darkMode);
    const { showAlert } = useAlert();

    const [signers, setSigners] = useState<SignerRow[]>([]);
    const [saving, setSaving] = useState(false);

    // O lead abre como primeiro signatário: é quem mais assina o que sai daqui.
    useEffect(() => {
        if (!attachment) return;

        setSigners([{ name: signerName ?? '', email: signerEmail ?? '' }]);
    }, [attachment?.id]);

    const field = {
        '& .MuiOutlinedInput-root': {
            bgcolor: cc.surfaceBg,
            borderRadius: '8px',
            color: cc.textPrimary,
            '& fieldset': { borderColor: cc.border },
        },
        '& .MuiInputBase-input': { color: cc.textPrimary },
        '& .MuiFormLabel-root': { color: cc.textSecondary },
    };

    const change = (index: number, key: keyof SignerRow, value: string) =>
        setSigners((current) => current.map((row, i) => (i === index ? { ...row, [key]: value } : row)));

    const send = async () => {
        if (!attachment) return;

        setSaving(true);

        try {
            await new LeadSignatureService().send(leadId, {
                attachmentId: attachment.id,
                signers: signers.map((row) => ({ name: row.name.trim(), email: row.email.trim() })),
            });
            showAlert(t('assinatura.enviadoSucesso'), 'success', 2500);
            onSent();
            onClose();
        } catch (err) {
            showAlert(apiErrorMessage(err), 'error', 5000);
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={attachment !== null} onClose={onClose} fullWidth maxWidth="md">
            <DialogTitle sx={{ pb: 0.5 }}>
                {t('assinatura.titulo')}
                <Typography variant="caption" sx={{ display: 'block', color: cc.textSecondary }}>
                    {attachment?.fileName}
                </Typography>
            </DialogTitle>

            <DialogContent dividers sx={{ display: 'grid', gap: 1.5, pt: 2 }}>
                <Typography sx={{ fontSize: 12.5, color: cc.textMuted }}>
                    {t('assinatura.nomeAjuda')} {t('assinatura.emailAjuda')}
                </Typography>

                {signers.map((row, index) => (
                    <Box key={index} sx={{ display: 'flex', gap: 1.25, alignItems: 'center' }}>
                        <TextField
                            fullWidth
                            autoFocus={index === signers.length - 1}
                            size="small"
                            label={t('assinatura.nome')}
                            placeholder={t('assinatura.nomePlaceholder')}
                            value={row.name}
                            onChange={(e) => change(index, 'name', e.target.value)}
                            sx={field}
                        />
                        <TextField
                            fullWidth
                            size="small"
                            type="email"
                            label={t('assinatura.email')}
                            placeholder={t('assinatura.emailPlaceholder')}
                            value={row.email}
                            onChange={(e) => change(index, 'email', e.target.value)}
                            sx={field}
                        />
                        <Tooltip title={t('assinatura.remover')}>
                            <span>
                                <IconButton
                                    size="small"
                                    disabled={signers.length === 1}
                                    onClick={() => setSigners((current) => current.filter((_, i) => i !== index))}
                                    sx={{ color: cc.textMuted }}
                                >
                                    <DeleteOutlineIcon sx={{ fontSize: '18px !important' }} />
                                </IconButton>
                            </span>
                        </Tooltip>
                    </Box>
                ))}

                {signers.length < MAX_SIGNERS && (
                    <Box
                        role="button"
                        tabIndex={0}
                        onClick={() => setSigners((current) => [...current, { name: '', email: '' }])}
                        onKeyDown={(e) =>
                            e.key === 'Enter' && setSigners((current) => [...current, { name: '', email: '' }])}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 0.75,
                            height: 36,
                            border: `1px dashed ${cc.borderStrong}`,
                            borderRadius: '9px',
                            color: cc.textSecondary,
                            fontSize: 12.5,
                            fontWeight: 600,
                            cursor: 'pointer',
                        }}
                    >
                        <AddIcon sx={{ fontSize: 16 }} />
                        {t('assinatura.adicionar')}
                    </Box>
                )}
            </DialogContent>

            <DialogActions>
                <MDButton variant="gradient" color="dark" onClick={onClose}>
                    {t('general.cancelar')}
                </MDButton>
                <MDButton variant="gradient" color="success" onClick={send} disabled={saving}>
                    {t('assinatura.enviar')}
                </MDButton>
            </DialogActions>
        </Dialog>
    );
};
